import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { BillingDetails, BillingResponse, RepaymentTermsResponse, ContractBillingTxnResponse, APRConfigResponse, APRConfig } from '../models/billing.model';
import { CLPContactSOInventionResponse } from '../models/contact.model';
import { PromotionOffersResponse } from '../models/promotionOffers.model';
import { ContractPaymentResponse, ContractMultiPayment, ContractMultiPaymentResponse } from '../models/contractPayment.model';
import { delayedRetryHttp } from '../shared/delayedRetry';
import { UtilityService } from './shared/utility.service';
import { AllocatedFileNumberDetailsResponse, ContractNumberResponse, ContractNumResponse } from '../models/contractNum.model';

@Injectable()
export class BillingService {
    private baseUrl: string;
    private api: string = "api/BillPayment";

    constructor(private httpClient: HttpClient, @Inject('BASE_URL') _baseUrl: string, private _utilityService: UtilityService) {
        this.baseUrl = _baseUrl + this.api;
    }

    private handleErrors(errorResponse: HttpErrorResponse) {
        if (errorResponse.error instanceof ErrorEvent) {
            console.error('Billing Service Client Side Error: ', errorResponse.error.message);
        } else {
            throw errorResponse;
        }
    }

    async payCardPaymentAsync(encryptedUser: string, billingDetails: BillingDetails): Promise<BillingResponse | void> {
        const a = await this.httpClient.post<BillingResponse>(this.baseUrl, billingDetails, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, billingDetails, "", encryptedUser, "BillingService", "payCardPaymentAsync"); });

        return a;
    }

    async payAmountAsync(encryptedUser: string, billingDetails: BillingDetails): Promise<BillingResponse | void> {
        const a = await this.httpClient.post<BillingResponse>(`${this.baseUrl}/SaveBillingTxn`, billingDetails, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, billingDetails, "", encryptedUser, "BillingService", "payAmountAsync"); });

        return a;
    }

    async getAmountAsync(encryptedUser: string, contractSessionId: number): Promise<BillingResponse | void> {
        const a = await this.httpClient
            .get<BillingResponse>(`${this.baseUrl}/GetAmount/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "BillingService", "getAmountAsync"); });

        return a;
    }

    async getPaymentDetailsAsync(encryptedUser: string, contractSessionId: number): Promise<ContractBillingTxnResponse | void> {
        const a = await this.httpClient
            .get<ContractBillingTxnResponse>(`${this.baseUrl}/GetPaymentDetails/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "BillingService", "getPaymentDetailsAsync"); });

        return a;
    }

    async getCLPContact_GetBipSubInventionAsync(encryptedUser: string, contactId: number): Promise<CLPContactSOInventionResponse | void> {
        const a = await this.httpClient
            .get<CLPContactSOInventionResponse>(`${this.baseUrl}/CLPContact_GetBipSubInvention/${contactId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contactId - " + contactId, encryptedUser, "BillingService", "getCLPContact_GetBipSubInventionAsync"); });

        return a;
    }

    async getContractNum_GetAsync(encryptedUser: string, contractSessionId: number): Promise<ContractNumberResponse | void> {
        const a = await this.httpClient
            .get<ContractNumberResponse>(`${this.baseUrl}/ContractNum_Get/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "BillingService", "getContractNum_GetAsync"); });

        return a;
    }

    async ContractPayment_CreateUpdateAsync(encryptedUser: string, contractPaymentResponse: ContractPaymentResponse): Promise<ContractPaymentResponse | void> {
        const a = await this.httpClient.post<ContractPaymentResponse>(`${this.baseUrl}/ContractPayment_CreateUpdate`, contractPaymentResponse, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractPaymentResponse, "", encryptedUser, "BillingService", "ContractPayment_CreateUpdateAsync"); });

        return a;
    }

    async ContractMultiPayment_CreateAsync(encryptedUser: string, contractMultiPaymentResponse: ContractMultiPaymentResponse): Promise<ContractMultiPaymentResponse | void> {
        const a = await this.httpClient.post<ContractMultiPaymentResponse>(`${this.baseUrl}/ContractMultiPayment_Create`, contractMultiPaymentResponse, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractMultiPaymentResponse, "", encryptedUser, "BillingService", "ContractMultiPayment_CreateAsync"); });

        return a;
    }

    async getRepaymentTermsAsync(encryptedUser: string, countryID: number, productID: number): Promise<RepaymentTermsResponse | void> {
        const a = await this.httpClient
            .get<RepaymentTermsResponse>(`${this.baseUrl}/GetRepaymentTerms/${countryID}/${productID}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "", encryptedUser, "BillingService", "getRepaymentTermsAsync"); });

        return a;
    }

    async getPromotionOffersAsync(encryptedUser: string): Promise<PromotionOffersResponse | void> {
        const a = await this.httpClient
            .get<PromotionOffersResponse>(`${this.baseUrl}/GetPromotionOffers/`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "", encryptedUser, "BillingService", "getPromotionOffersAsync"); });

        return a;
    }

    async getContractPaymentAsync(encryptedUser: string, inventionId: number, productId: number, contractSessionId: number): Promise<ContractPaymentResponse | void> {
        const a = await this.httpClient
            .get<ContractPaymentResponse>(`${this.baseUrl}/ContractPaymentGet/${inventionId}/${productId}/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "inventionId - " + inventionId + " productId - " + productId + " contractSessionId - " + contractSessionId, encryptedUser, "BillingService", "getContractPaymentAsync"); });

        return a;
    }


    async getContractMultiPaymentAsync(encryptedUser: string, contractSessionId: number): Promise<ContractMultiPaymentResponse | void> {
        const a = await this.httpClient
            .get<ContractMultiPaymentResponse>(`${this.baseUrl}/ContractMultiPayment_Get/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, " contractSessionId - " + contractSessionId, encryptedUser, "BillingService", "getContractMultiPaymentAsync"); });

        return a;
    }

    async GetCalculatedInstalmentAsync(encryptedUser: string, contractPaymentResponse: ContractPaymentResponse): Promise<ContractPaymentResponse | void> {
        const a = await this.httpClient.post<ContractPaymentResponse>(`${this.baseUrl}/GetCalculatedInstalment/`, contractPaymentResponse, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractPaymentResponse, "", encryptedUser, "BillingService", "GetCalculatedInstalmentAsync"); });


        return a;
    }

    async getFileNoByUserIdAsync(encryptedUser: string, clpUserId: number, contractSessionId: number): Promise<ContractNumResponse | void> {
        const a = await this.httpClient
            .get<ContractNumResponse>(`${this.baseUrl}/ContractNum_GetFileNoByUserId/${clpUserId}/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "clpUserId - " + clpUserId + " contractSessionId - " + contractSessionId, encryptedUser, "BillingService", "getFileNoByUserIdAsync"); });

        return a;
    }

    async getDuplicateFileNoAsync(encryptedUser: string, clpUserId: number, contractNumber: number): Promise<boolean | void> {
        const a = await this.httpClient
            .get<boolean>(`${this.baseUrl}/ContractNum_GetDuplicate/${clpUserId}/${contractNumber}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "clpUserId - " + clpUserId + " contractSessionId - " + contractNumber, encryptedUser, "BillingService", "getDuplicateFileNoAsync"); });

        return a;
    }

    async updateContractMultiPaymentAsync(encryptedUser: string, contractMultiPayment: ContractMultiPayment): Promise<ContractMultiPayment | void> {
        const a = await this.httpClient.post<ContractMultiPayment>(`${this.baseUrl}/ContractMultiPayment_Update/`, contractMultiPayment, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractMultiPayment, "", encryptedUser, "BillingService", "updateContractMultiPaymentAsync"); });

        return a;
    }

    async deleteContractMultiPaymentAsync(encryptedUser: string, contractSessionId: number, id: number): Promise<ContractMultiPayment | void> {
        const a = await this.httpClient.post<ContractMultiPayment>(`${this.baseUrl}/ContractMultiPayment_Delete/${contractSessionId}/${id}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId + " id - " + id, encryptedUser, "BillingService", "deleteContractMultiPaymentAsync"); });

        return a;
    }

    async sendReceiptEmailAsync(encryptedUser: string, clpUserId: number, contractSessionId: number): Promise<void | void> {
        const a = await this.httpClient.post<void>(`${this.baseUrl}/SendReceiptEmail/${clpUserId}/${contractSessionId}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId + " clpUserId - " + clpUserId, encryptedUser, "BillingService", "sendReceiptEmailAsync"); });

        return a;
    }

    async getPendingNetSuiteTransactionsAsync(encryptedUser: string, contractSessionId: number): Promise<boolean | void> {
        const a = await this.httpClient
            .get<boolean>(`${this.baseUrl}/GetPendingNetSuiteTransactions/${contractSessionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId, encryptedUser, "BillingService", "getPendingNetSuiteTransactionsAsync"); });
        return a;
    }

    async createNetSuiteTransactionsAsync(encryptedUser: string, contractSessionId: number): Promise<number | void> {
        const a = await this.httpClient.post<number>(`${this.baseUrl}/CreateNetSuiteTransactions/${contractSessionId}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractSessionId, "", encryptedUser, "BillingService", "createNetSuiteTransactions"); });

        return a;
    }

    async contractNumberUpdateAsync(encryptedUser: string, contractSessionId: number, fileNumber: string, loginCLPUserId: number, isOriginContracts: boolean = false): Promise<string | void> {
        const a = await this.httpClient.post<string>(`${this.baseUrl}/ContractNumber_Update/${contractSessionId}/${fileNumber}/${loginCLPUserId}/${isOriginContracts}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, contractSessionId, "", encryptedUser, "BillingService", "contractNumberUpdateAsync"); });

        return a;
    }

    async getAPRConfigAsync(encryptedUser: string): Promise<APRConfigResponse | void> {
        const a = await this.httpClient
            .get<APRConfigResponse>(`${this.baseUrl}/GetAPRConfig/`, {
                headers: new HttpHeaders({
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "", encryptedUser, "BillingService", "getAPRConfigAsync"); });

        return a;
    }

    async saveAPRConfigAsync(aprConfig: APRConfig[], encryptedUser: string, clpUserId: number): Promise<APRConfigResponse | void> {
        const a = await this.httpClient.post<APRConfigResponse>(`${this.baseUrl}/SaveAPRConfig/${clpUserId}`, aprConfig, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + encryptedUser
            })
        }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "", encryptedUser, "BillingService", "saveAPRConfigAsync"); });
        return a;
    }

    async getAllocatedFileNoDetailsAsync(encryptedUser: string, clpUserId: number, isPatentPlus: number): Promise<AllocatedFileNumberDetailsResponse | void> {
        const a = await this.httpClient
            .get<AllocatedFileNumberDetailsResponse>(`${this.baseUrl}/ContractNumber_GetAllocatedFileNo/${clpUserId}/${isPatentPlus}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "clpUserId - " + clpUserId, encryptedUser, "BillingService", "getAllocatedFileNoDetailsAsync"); });

        return a;
    }
}
